import { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Button, Input, Select } from "@legal-500/legal500-design-system";
import { usePublications } from "@/hooks/usePublications";
import { useNewAxisEditions } from "@/hooks/useNewAxisEditions";
import { useTaxonomyMode } from "@/hooks/useTaxonomyMode";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import type { CreateMailingGroupBody } from "@/api/mailing-groups.service";
import { newAxisEditionOptions } from "@/api/surveyTaxonomy.service";
import type { TaxonomyMode } from "@/api/submissions.service";
import { TaxonomyToggle } from "@/components/TaxonomyToggle";
import { BAR_GROUP_LABELS, publicationDisplayLabel } from "./mailingGroups.utils";
import { useRankingEntityPicker, useRegionPicker } from "./useRegionPicker";
import { useRefereesPreview } from "./useRefereesPreview";
import { RegionPickerSection } from "./RegionPickerSection";
import styles from "./MailingGroups.module.css";

type GenerateMailingGroupsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (body: CreateMailingGroupBody) => Promise<void>;
};

const FIRST_EDITION_YEAR = 2017;

const INTRO_TEXT =
  "Pick a publication and edition, then choose which regions to include. " +
  "Selected regions are merged into a single mailing group (e.g. to share a " +
  "common language). The bar regions are handled separately and cannot be " +
  "mixed with standard ones.";

function GenerateMailingGroupsModal({ isOpen, onClose, onSave }: GenerateMailingGroupsModalProps) {
  const currentYear = new Date().getFullYear();
  const { toggleVisible, mode: pageMode, ready: flagsReady } = useTaxonomyMode();

  const [mode, setMode] = useState<TaxonomyMode>(pageMode);
  const [name, setName] = useState("");
  const [editionYear, setEditionYear] = useState("");
  const [selectedPublicationId, setSelectedPublicationId] = useState("");
  const [selectedEditionId, setSelectedEditionId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const wasOpenRef = useRef(false);
  const modeChosenRef = useRef(false);

  const editionOptions = useMemo(() => {
    const years: { value: string; label: string }[] = [{ value: "", label: "Select Edition" }];
    for (let year = currentYear + 1; year >= FIRST_EDITION_YEAR; year--) {
      years.push({ value: String(year), label: String(year) });
    }
    return years;
  }, [currentYear]);

  const newAxis = mode === "new";

  const { data: publicationsData } = usePublications(
    { edition: editionYear || undefined },
    { enabled: !newAxis && !!editionYear },
  );

  const { data: newAxisEditions, error: newAxisEditionsError } = useNewAxisEditions({
    enabled: isOpen && newAxis,
  });

  const publicationOptions = useMemo(() => {
    if (newAxis) {
      return [
        { value: "", label: "Select Publication" },
        ...newAxisEditionOptions(newAxisEditions, editionYear),
      ];
    }
    const pubs = publicationsData?.publications ?? [];
    return [
      { value: "", label: "Select Publication" },
      ...pubs.map((p: LegacyPublication) => {
        const base = p.publication ? p.publication : `Publication ${p.id}`;
        return {
          value: String(p.id),
          label: publicationDisplayLabel(base, p.edition),
        };
      }),
    ];
  }, [newAxis, newAxisEditions, editionYear, publicationsData?.publications]);

  const selectedNewAxisEdition = useMemo(
    () => newAxisEditions?.find(edition => edition.editionId === selectedEditionId) ?? null,
    [newAxisEditions, selectedEditionId],
  );

  const selectedPublication = useMemo(() => {
    if (!selectedPublicationId || !publicationsData?.publications) return null;
    return (
      publicationsData.publications.find(
        (p: LegacyPublication) => String(p.id) === selectedPublicationId,
      ) ?? null
    );
  }, [selectedPublicationId, publicationsData?.publications]);

  const publicationIdNumber = selectedPublicationId ? Number(selectedPublicationId) : null;

  const legacyPicker = useRegionPicker({
    publicationId: publicationIdNumber,
    edition: editionYear || null,
    enabled: isOpen && !newAxis,
  });

  const rankingEntityPicker = useRankingEntityPicker({
    editionId: selectedEditionId || null,
    enabled: isOpen && newAxis,
  });

  const picker = newAxis ? rankingEntityPicker : legacyPicker;

  const selectedRegionIds = useMemo(
    () =>
      legacyPicker.selectedRegions
        .map(r => r.id)
        .filter((id): id is number => typeof id === "number"),
    [legacyPicker.selectedRegions],
  );

  const selectedRankingEntityIds = useMemo(
    () => rankingEntityPicker.selectedRegions.map(entity => entity.id),
    [rankingEntityPicker.selectedRegions],
  );

  const {
    previewLoading,
    previewError,
    uniqueEmails: uniqueEmailCount,
  } = useRefereesPreview(
    newAxis
      ? {
          editionId: selectedEditionId || null,
          rankingEntityIds: selectedRankingEntityIds,
          enabled: !picker.isMixed,
        }
      : {
          publicationId: publicationIdNumber,
          regionIds: selectedRegionIds,
          enabled: !picker.isMixed,
        },
  );

  // Reset all local state when the modal (re)opens.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      modeChosenRef.current = false;
      setMode(pageMode);
      setName("");
      setEditionYear(String(currentYear));
      setSelectedPublicationId("");
      setSelectedEditionId("");
      setSubmitting(false);
      setErrors({});
      legacyPicker.reset();
      rankingEntityPicker.reset();
    }
    wasOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentYear, pageMode]);

  const clearError = (field: string) => {
    setErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const applyMode = (next: TaxonomyMode) => {
    setMode(next);
    setSelectedPublicationId("");
    setSelectedEditionId("");
    legacyPicker.reset();
    rankingEntityPicker.reset();
    setErrors({});
  };

  useEffect(() => {
    if (isOpen && flagsReady && !modeChosenRef.current && pageMode !== mode) {
      applyMode(pageMode);
    }
  }, [isOpen, flagsReady, pageMode, mode]);

  const handleTaxonomyChange = (next: TaxonomyMode) => {
    modeChosenRef.current = true;
    applyMode(next);
  };

  const handleEditionChange = (value: string) => {
    setEditionYear(value);
    setSelectedPublicationId("");
    setSelectedEditionId("");
    picker.reset();
    clearError("edition");
    clearError("publication");
  };

  const handlePublicationChange = (value: string) => {
    if (newAxis) {
      setSelectedEditionId(value);
    } else {
      setSelectedPublicationId(value);
    }
    picker.reset();
    clearError("publication");
  };

  const handleToggleRegion = (region: Parameters<typeof picker.toggleRegion>[0]) => {
    picker.toggleRegion(region);
    clearError("regions");
  };

  const handleSelectAll = () => {
    picker.selectAllRegions();
    clearError("regions");
  };

  const handleSelectNonBar = () => {
    picker.selectNonBarRegions();
    clearError("regions");
  };

  const handleSelectBar = () => {
    picker.selectBarRegions();
    clearError("regions");
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Group name is required";
    if (!editionYear) next.edition = "Edition is required";
    if (newAxis ? !selectedNewAxisEdition : !selectedPublicationId) {
      next.publication = "Publication is required";
    }
    if (picker.selectedRegions.length === 0) next.regions = "Select at least one region";
    if (picker.selectionKind === "mixed") {
      next.regions = "Bar and standard regions cannot be mixed in one group";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildBody = (): CreateMailingGroupBody | null => {
    if (newAxis) {
      if (!selectedNewAxisEdition) return null;
      return {
        editionId: selectedNewAxisEdition.editionId,
        publicationName: selectedNewAxisEdition.publication,
        publicationEdition: selectedNewAxisEdition.editionName || undefined,
        edition: editionYear,
        name: name.trim(),
        regions: rankingEntityPicker.selectedRegions.map(entity => ({
          rankingEntityId: entity.id,
          regionName: entity.name,
        })),
      };
    }
    if (!selectedPublication) return null;
    return {
      publicationId: Number(selectedPublicationId),
      publicationName: selectedPublication.publication ?? undefined,
      publicationEdition: selectedPublication.edition ?? undefined,
      edition: editionYear,
      name: name.trim(),
      regions: legacyPicker.selectedRegions.map(region => ({
        regionId: region.id as number,
        regionName: region.name ?? undefined,
      })),
    };
  };

  const handleSave = async () => {
    if (!validate()) return;
    const body = buildBody();
    if (!body) return;

    // On failure the error propagates to the parent (which surfaces the toast,
    // incl. the 400 mix error) and the modal stays open so the user can correct
    // the selection. `finally` resets the submitting flag either way.
    try {
      setSubmitting(true);
      await onSave(body);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const regionsDisabled = newAxis ? !selectedEditionId : !selectedPublicationId;
  const canEditRegions = !regionsDisabled && picker.regions.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate mailing groups"
      size="mediumLarge"
      backdropClick={false}
    >
      <div className={styles.modalForm}>
        <p className={styles.modalIntro}>{INTRO_TEXT}</p>

        {toggleVisible && (
          <TaxonomyToggle mode={mode} onChange={handleTaxonomyChange} disabled={submitting} />
        )}

        <div className={styles.formRowPublication}>
          <div className={styles.formFieldPublication}>
            <Select
              label="Publication"
              options={publicationOptions}
              value={newAxis ? selectedEditionId : selectedPublicationId}
              onChange={e => handlePublicationChange(e.target.value)}
              fullWidth
              required
              disabled={!editionYear}
              error={
                newAxis && newAxisEditionsError
                  ? "Failed to load editions. Please try again."
                  : errors.publication
              }
            />
          </div>
          <div className={styles.formFieldEdition}>
            <Select
              label="Edition"
              options={editionOptions}
              value={editionYear}
              onChange={e => handleEditionChange(e.target.value)}
              fullWidth
              required
              error={errors.edition}
            />
          </div>
        </div>

        <RegionPickerSection
          regions={picker.regions}
          selectedRegionKeys={picker.selectedRegionKeys}
          usedRegionsById={picker.usedRegionsById}
          isRegionUsed={picker.isRegionUsed}
          isRegionSelectable={picker.isRegionSelectable}
          isBar={picker.isBar}
          onToggle={handleToggleRegion}
          onSelectAll={handleSelectAll}
          onClear={picker.clearRegions}
          showActions={canEditRegions}
          onSelectNonBar={handleSelectNonBar}
          onSelectBar={handleSelectBar}
          countriesLoading={picker.countriesLoading}
          countriesError={picker.countriesError}
          disabledHint={regionsDisabled ? "Select a publication to load its regions." : undefined}
          usedRegionLabelMode="groupName"
          error={errors.regions}
        />

        {picker.isMixed && (
          <div className={styles.warningBanner} role="alert">
            Bar and standard regions cannot be mixed in a single mailing group. Deselect one type to
            continue.
          </div>
        )}

        {!picker.isMixed && picker.isAllBar && picker.selectedRegions.length > 0 && (
          <div className={styles.infoBanner}>
            Bar regions produce 2 mailing groups each ({BAR_GROUP_LABELS.join(" / ")}).
          </div>
        )}

        <div className={styles.formFieldFull}>
          <Input
            label="Group name"
            value={name}
            onChange={e => {
              setName(e.target.value);
              clearError("name");
            }}
            fullWidth
            placeholder="e.g. DACH, Nordics, Benelux…"
            helperText="Give the group a recognisable name."
            required
            error={errors.name}
          />
        </div>

        <div className={styles.countSummaryRow}>
          <div className={styles.countSummary}>
            <span className={styles.countLabel}>Mailing groups to be generated</span>
            <span className={styles.countValue} data-testid="group-count">
              {picker.groupCount}
            </span>
          </div>
          <div className={styles.countSummary}>
            <span className={styles.countLabel}>Unique emails in selection</span>
            <span className={styles.countValue} data-testid="unique-email-count">
              {uniqueEmailCount}
            </span>
          </div>
        </div>

        {/* Always rendered so idle/loading/error keep the same height — a
            conditionally mounted line re-centers the modal ("jump"). */}
        <p
          className={previewError ? styles.previewHintError : styles.previewHint}
          aria-live="polite"
        >
          {previewLoading ? "Loading preview…" : previewError ? previewError : " "}
        </p>

        <div className={styles.modalFooter}>
          <Button variant="outlined" size="small" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSave}
            disabled={submitting || picker.isMixed || picker.groupCount === 0}
          >
            {submitting ? "Generating…" : "Confirm"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export { GenerateMailingGroupsModal };
